
import type { TileMapRenderer } from '../renderer/tilemap/TileMapRenderer';

const CAMERA_PAN_SPEED_PIXELS_PER_SECOND = 720;
const MAX_FRAME_DELTA_SECONDS = 0.05;

export class CameraController {
    private readonly renderer: TileMapRenderer;
    private isTracking = true;
    private isSnapKeyHeld = false;
    private disposed = false;
    private animationFrameId: number | null = null;
    private lastFrameTimestamp: number | null = null;

    // Track active keys for smooth diagonal movement
    private activeKeys = new Set<string>();
    private rotateKeys = new Set<string>();
    private pendingSnapToPlayer = false;

    constructor(renderer: TileMapRenderer) {
        this.renderer = renderer;

        // Bind methods
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleKeyUp = this.handleKeyUp.bind(this);
        this.updateLoop = this.updateLoop.bind(this);
        this.handleBlur = this.handleBlur.bind(this);

        // Attach listeners
        window.addEventListener('keydown', this.handleKeyDown, true);
        window.addEventListener('keyup', this.handleKeyUp, true);
        window.addEventListener('blur', this.handleBlur);

        // Start loop
        this.startLoop();
    }

    public panTo(x: number, y: number): void {
        this.renderer.setCameraFocus({ x, y });
        this.isTracking = false;
        this.activeKeys.clear();
    }

    public dispose(): void {
        this.disposed = true;
        window.removeEventListener('keydown', this.handleKeyDown, true);
        window.removeEventListener('keyup', this.handleKeyUp, true);
        window.removeEventListener('blur', this.handleBlur);
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
        }
    }

    private startLoop(): void {
        if (this.disposed) return;
        this.animationFrameId = requestAnimationFrame(this.updateLoop);
    }

    private updateLoop(timestamp: number): void {
        if (this.disposed) return;

        const deltaSeconds = this.lastFrameTimestamp === null
            ? 1 / 60
            : Math.min((timestamp - this.lastFrameTimestamp) / 1000, MAX_FRAME_DELTA_SECONDS);
        this.lastFrameTimestamp = timestamp;

        this.updateCamera(deltaSeconds);
        this.animationFrameId = requestAnimationFrame(this.updateLoop);
    }

    private updateCamera(deltaSeconds: number): void {
        const currentCamera = this.renderer.getCameraFocus();
        const playerPos = this.renderer.getVisualCharacterPosition();

        if (!currentCamera || !playerPos) return;

        if (this.isSnapKeyHeld) {
            if (currentCamera.x !== playerPos.x || currentCamera.y !== playerPos.y) {
                this.renderer.setCameraFocus(playerPos);
            }
            return;
        }

        if (this.activeKeys.size === 0) {
            if (this.isTracking) {
                if (currentCamera.x !== playerPos.x || currentCamera.y !== playerPos.y) {
                    this.renderer.setCameraFocus(playerPos);
                }
            }
            return;
        }

        this.isTracking = false;

        let dx = 0;
        let dy = 0;

        if (this.activeKeys.has('ArrowUp')) dy -= 1;
        if (this.activeKeys.has('ArrowDown')) dy += 1;
        if (this.activeKeys.has('ArrowLeft')) dx -= 1;
        if (this.activeKeys.has('ArrowRight')) dx += 1;

        if (dx !== 0 || dy !== 0) {
            const magnitude = Math.hypot(dx, dy) || 1;
            const distance = CAMERA_PAN_SPEED_PIXELS_PER_SECOND * deltaSeconds;
            this.renderer.panCameraByScreen((dx / magnitude) * distance, (dy / magnitude) * distance);
        }
    }

    private handleKeyDown(event: KeyboardEvent): void {
        if (
            event.target instanceof HTMLInputElement
            || event.target instanceof HTMLTextAreaElement
            || event.target instanceof HTMLSelectElement
            || (event.target instanceof HTMLElement && (
                event.target.isContentEditable
                || event.target.closest('input, textarea, select, [contenteditable="true"]') !== null
            ))
        ) {
            return;
        }

        const normalizedKey = event.key.length === 1 ? event.key.toLowerCase() : event.key;

        if ((normalizedKey === 'q' || normalizedKey === 'e') && !event.repeat) {
            if (!this.rotateKeys.has(normalizedKey)) {
                this.renderer.rotateCameraOrbit(normalizedKey === 'q' ? 'left' : 'right');
                this.rotateKeys.add(normalizedKey);
            }
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return;
        }

        if ((event.key === ' ' || event.code === 'Space') && !event.repeat) {
            this.isSnapKeyHeld = true;
            this.pendingSnapToPlayer = true;
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return;
        }

        if (
            ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
            && !event.shiftKey
            && !event.altKey
            && !event.ctrlKey
            && !event.metaKey
        ) {
            if (this.pendingSnapToPlayer) {
                this.pendingSnapToPlayer = false;
                return;
            }
            this.activeKeys.add(event.key);
        }
    }

    private handleKeyUp(event: KeyboardEvent): void {
        if (
            event.target instanceof HTMLInputElement
            || event.target instanceof HTMLTextAreaElement
            || event.target instanceof HTMLSelectElement
            || (event.target instanceof HTMLElement && (
                event.target.isContentEditable
                || event.target.closest('input, textarea, select, [contenteditable="true"]') !== null
            ))
        ) {
            return;
        }
        const normalizedKey = event.key.length === 1 ? event.key.toLowerCase() : event.key;
        if (normalizedKey === 'q' || normalizedKey === 'e') {
            this.rotateKeys.delete(normalizedKey);
            return;
        }
        if (event.key === ' ' || event.code === 'Space') {
            if (this.pendingSnapToPlayer) {
                this.snapToPlayer(false);
            }
            this.isSnapKeyHeld = false;
            this.pendingSnapToPlayer = false;
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return;
        }
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
            this.activeKeys.delete(event.key);
        }
    }

    private handleBlur(): void {
        this.activeKeys.clear();
        this.rotateKeys.clear();
        this.isSnapKeyHeld = false;
        this.pendingSnapToPlayer = false;
        this.lastFrameTimestamp = null;
    }

    private snapToPlayer(enableTracking: boolean): void {
        const playerPos = this.renderer.getVisualCharacterPosition();
        if (playerPos) {
            this.renderer.setCameraFocus(playerPos);
            this.isTracking = enableTracking;
        }
    }
}
